import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Project, ProjectResource } from '@/features/Projects/models/project.types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export interface ProposalPdfData {
  project: Project;
  client: {
    name: string;
    contact?: string;
    email?: string;
    company?: string;
  };
  team: {
    name: string;
    role: string;
    experience: string;
  }[];
  timeline: {
    phase: string;
    duration: string;
    description: string;
  }[];
  investment: {
    totalValue: number;
    paymentTerms: string;
    validUntil: string;
  };
  nextSteps: string[];
}

export class ProposalPdfService {
  private readonly RARO_BLUE = '#0066CC';
  private readonly RARO_GRAY = '#666666';
  private readonly RARO_LIGHT_GRAY = '#F5F5F5';

  async generateProposalPdf(data: ProposalPdfData): Promise<Blob> {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;

    // Configurar fontes
    doc.setFont('helvetica');

    // Página 1 - Capa
    this.addHeader(doc, pageWidth, margin);
    this.addClientInfo(doc, data.client, margin, 80);
    this.addProjectScope(doc, data.project, margin, 120);

    // Nova página - Metodologia e Timeline
    doc.addPage();
    this.addHeader(doc, pageWidth, margin);
    this.addMethodology(doc, data.project, margin, 50);
    this.addTimeline(doc, data.timeline, margin, 120);

    // Nova página - Equipe e Investimento
    doc.addPage();
    this.addHeader(doc, pageWidth, margin);
    this.addTeam(doc, data.team, margin, 50);
    this.addInvestment(doc, data.investment, margin, 150);

    // Nova página - Próximos Passos
    doc.addPage();
    this.addHeader(doc, pageWidth, margin);
    this.addNextSteps(doc, data.nextSteps, margin, 50);
    this.addFooter(doc, pageHeight, margin);

    return new Promise((resolve) => {
      const pdfBlob = doc.output('blob');
      resolve(pdfBlob);
    });
  }

  private addHeader(doc: jsPDF, pageWidth: number, margin: number) {
    // Logo e header da Raro Labs
    const [r0, g0, b0] = this.hexToRgb(this.RARO_BLUE);
    doc.setFillColor(r0, g0, b0);
    doc.rect(0, 0, pageWidth, 60, 'F');

    // Logo (simulado como texto)
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('RARO LABS', margin, 25);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Soluções em Tecnologia e Inovação', margin, 40);

    // Reset cor do texto
    doc.setTextColor(0, 0, 0);
  }

  private addClientInfo(doc: jsPDF, client: ProposalPdfData['client'], margin: number, y: number) {
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('PROPOSTA COMERCIAL', margin, y);

    y += 15;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Cliente:', margin, y);

    y += 8;
    doc.setFont('helvetica', 'normal');
    doc.text(client.name, margin, y);

    if (client.company) {
      y += 6;
      doc.text(client.company, margin, y);
    }

    if (client.contact) {
      y += 6;
      doc.text(`Contato: ${client.contact}`, margin, y);
    }

    if (client.email) {
      y += 6;
      doc.text(`E-mail: ${client.email}`, margin, y);
    }

    y += 8;
    doc.text(`Data: ${format(new Date(), 'dd/MM/yyyy', { locale: ptBR })}`, margin, y);
  }

  private addProjectScope(doc: jsPDF, project: Project, margin: number, y: number) {
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const [r1, g1, b1] = this.hexToRgb(this.RARO_BLUE);
    doc.setTextColor(r1, g1, b1);
    doc.text('ESCOPO DO PROJETO', margin, y);
    doc.setTextColor(0, 0, 0);

    y += 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(project.name, margin, y);

    y += 10;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');

    // Dividir descrição em linhas
    const description = project.description || '';
    const descriptionLines = doc.splitTextToSize(description, 170);
    doc.text(descriptionLines, margin, y);

    // Objetivos se disponíveis
    if (project.objectives && project.objectives.length > 0) {
      y += descriptionLines.length * 5 + 10;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Objetivos:', margin, y);

      y += 8;
      doc.setFont('helvetica', 'normal');
      project.objectives.forEach((objective, index) => {
        const objectiveLines = doc.splitTextToSize(`• ${objective}`, 160);
        doc.text(objectiveLines, margin + 5, y);
        y += objectiveLines.length * 5 + 2;
      });
    }
  }

  private addMethodology(doc: jsPDF, project: Project, margin: number, y: number) {
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const [r2, g2, b2] = this.hexToRgb(this.RARO_BLUE);
    doc.setTextColor(r2, g2, b2);
    doc.text('METODOLOGIA', margin, y);
    doc.setTextColor(0, 0, 0);

    y += 15;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');

    const methodology = `
Nossa metodologia combina agilidade e qualidade, seguindo as melhores práticas do mercado:

• Análise e Planejamento: Compreensão profunda dos requisitos e definição da arquitetura
• Desenvolvimento Iterativo: Entregas incrementais com feedback contínuo
• Testes Integrados: Qualidade assegurada em cada etapa do desenvolvimento
• Deploy Automatizado: Entregas rápidas e confiáveis
• Suporte Continuado: Acompanhamento pós-entrega e manutenção

Utilizamos tecnologias modernas e comprovadas, garantindo escalabilidade, performance e segurança.`;

    const methodologyLines = doc.splitTextToSize(methodology.trim(), 170);
    doc.text(methodologyLines, margin, y);
  }

  private addTimeline(doc: jsPDF, timeline: ProposalPdfData['timeline'], margin: number, y: number) {
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const [r3, g3, b3] = this.hexToRgb(this.RARO_BLUE);
    doc.setTextColor(r3, g3, b3);
    doc.text('CRONOGRAMA', margin, y);
    doc.setTextColor(0, 0, 0);

    y += 15;

    timeline.forEach((phase, index) => {
      // Background para cada fase
      doc.setFillColor(245, 245, 245);
      doc.rect(margin, y - 5, 170, 20, 'F');

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`Fase ${index + 1}: ${phase.phase}`, margin + 5, y + 3);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Duração: ${phase.duration}`, margin + 5, y + 10);

      y += 25;
      const descriptionLines = doc.splitTextToSize(phase.description, 160);
      doc.text(descriptionLines, margin + 5, y);

      y += descriptionLines.length * 4 + 10;
    });
  }

  private addTeam(doc: jsPDF, team: ProposalPdfData['team'], margin: number, y: number) {
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const [r4, g4, b4] = this.hexToRgb(this.RARO_BLUE);
    doc.setTextColor(r4, g4, b4);
    doc.text('EQUIPE', margin, y);
    doc.setTextColor(0, 0, 0);

    y += 15;

    team.forEach((member) => {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(member.name, margin, y);

      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.text(`Cargo: ${member.role}`, margin, y);

      y += 5;
      const experienceLines = doc.splitTextToSize(`Experiência: ${member.experience}`, 160);
      doc.text(experienceLines, margin, y);

      y += experienceLines.length * 5 + 10;
    });
  }

  private addInvestment(doc: jsPDF, investment: ProposalPdfData['investment'], margin: number, y: number) {
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const [r5, g5, b5] = this.hexToRgb(this.RARO_BLUE);
    doc.setTextColor(r5, g5, b5);
    doc.text('INVESTIMENTO', margin, y);
    doc.setTextColor(0, 0, 0);

    y += 15;

    // Valor total destacado
    doc.setFillColor(245, 245, 245);
    doc.rect(margin, y - 5, 170, 25, 'F');

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('VALOR TOTAL:', margin + 5, y + 5);

    const formattedValue = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(investment.totalValue);

    const [r6, g6, b6] = this.hexToRgb(this.RARO_BLUE);
    doc.setTextColor(r6, g6, b6);
    doc.text(formattedValue, margin + 5, y + 15);
    doc.setTextColor(0, 0, 0);

    y += 35;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Condições de Pagamento:', margin, y);

    y += 8;
    doc.setFont('helvetica', 'normal');
    const paymentLines = doc.splitTextToSize(investment.paymentTerms, 160);
    doc.text(paymentLines, margin, y);

    y += paymentLines.length * 5 + 10;

    doc.setFont('helvetica', 'bold');
    doc.text(`Proposta válida até: ${investment.validUntil}`, margin, y);
  }

  private addNextSteps(doc: jsPDF, nextSteps: string[], margin: number, y: number) {
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const [r7, g7, b7] = this.hexToRgb(this.RARO_BLUE);
    doc.setTextColor(r7, g7, b7);
    doc.text('PRÓXIMOS PASSOS', margin, y);
    doc.setTextColor(0, 0, 0);

    y += 15;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');

    nextSteps.forEach((step, index) => {
      const stepLines = doc.splitTextToSize(`${index + 1}. ${step}`, 160);
      doc.text(stepLines, margin, y);
      y += stepLines.length * 5 + 5;
    });

    y += 20;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Ficamos à disposição para esclarecimentos!', margin, y);

    y += 15;
    doc.setFont('helvetica', 'normal');
    doc.text('Atenciosamente,', margin, y);

    y += 10;
    doc.setFont('helvetica', 'bold');
    doc.text('Equipe Raro Labs', margin, y);
  }

  private addFooter(doc: jsPDF, pageHeight: number, margin: number) {
    const footerY = pageHeight - 20;

    const [r8, g8, b8] = this.hexToRgb(this.RARO_BLUE);
    doc.setFillColor(r8, g8, b8);
    doc.rect(0, footerY - 5, doc.internal.pageSize.getWidth(), 25, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('www.rarolabs.com.br | comunicacao@rarolabs.com.br | (11) 9999-9999', margin, footerY + 5);
  }

  private hexToRgb(hex: string): [number, number, number] {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
      ]
      : [0, 0, 0];
  }
}

export const proposalPdfService = new ProposalPdfService();